// ═══ LA PUERTA DE ACCESO: QUIÉN ENTRA A LA PLATAFORMA ════════════════════════
//
// UNA SOLA RESPUESTA PARA DOS MUNDOS. La pregunta la hace el navegador —para
// decidir si pinta la aplicación o "Tu cuenta todavía no tiene acceso"— y la hace
// el servidor —`api/axior.js`, que no llama al modelo de IA para quien no pasa—.
// Si cada uno tuviera su copia, el día que discreparan alguien vería la puerta
// cerrada y podría seguir usando AXIOR por detrás, o al revés, y nadie las vería
// discrepar porque cada una corre en un sitio distinto. Por eso App.tsx NO tiene
// copia: importa de aquí.
//
// TRES RAMAS, Y SOLO TRES (más el interruptor general):
//   · administrador, por correo (la lista está en `admin.js`, también compartida);
//   · el interruptor general `configuracion/beta_pro.activo === true`;
//   · patrón cuyo correo está en `usuarios_pro` con `activo === true`;
//   · trabajador cuyo EMPLEADOR está en esa lista. Es una herencia, no una copia:
//     si el patrón deja de estar autorizado, su gente deja de entrar con él.
//
// POR OMISIÓN SE CIERRA. Si el documento no existe, no se puede leer o viene
// vacío, la respuesta es NO.
//
// NO HAY NADA DE REACT NI DE FIREBASE AQUÍ. Quien llama pasa `leer(coleccion, id)`:
// devuelve los datos del documento, `null` si no existe, y LANZA si no se pudo
// leer. El navegador lo hace con `getDoc` y el servidor con firebase-admin; la
// decisión es la misma línea por línea.
const { esAdmin } = require("./admin.js");

// El correo, siempre normalizado igual: es el id de los documentos de la lista y
// de las cuentas de trabajador, así que una mayúscula de más sería otra ruta.
function correoClave(email) {
  return String(email || "").toLowerCase().trim();
}

const ACCESO_DENEGADO = { entra: false, via: "ninguna", motivo: "no_autorizado" };

// Una lectura que falla cuenta como "no existe": se cierra.
async function _leerSeguro(leer, coleccion, id) {
  try { return (await leer(coleccion, id)) || null; } catch (e) { return null; }
}

// EL INTERRUPTOR GENERAL: solo el `true` explícito abre.
async function accesoAbiertoATodos(leer) {
  const cfg = await _leerSeguro(leer, "configuracion", "beta_pro");
  return !!cfg && cfg.activo === true;
}

// ¿Este correo está en la lista de autorizados? El correo es el id: se lee una
// ruta conocida, no se corre una consulta.
async function estaEnLaLista(leer, correo) {
  if (!correo) return false;
  const d = await _leerSeguro(leer, "usuarios_pro", correo);
  return !!d && d.activo === true;
}

// El correo del patrón de un trabajador. `empleadorEmail` se guarda desde que se
// crea la cuenta; para las cuentas anteriores a ese campo se resuelve por el uid.
async function empleadorDeTrabajador(leer, correo) {
  let data;
  try { data = await leer("cuentas_trabajador", correo); } catch (e) { return { email: "", existe: false }; }
  if (!data) return { email: "", existe: false };
  if (data.activo === false) return { email: "", existe: false };   // cuenta suspendida
  const directo = correoClave(data.empleadorEmail);
  if (directo) return { email: directo, existe: true };
  if (data.empleadorId) {
    const u = await _leerSeguro(leer, "usuarios", data.empleadorId);   // sin permiso: no hereda
    if (u) return { email: correoClave(u.email), existe: true };
  }
  return { email: "", existe: true };
}

// `user` es lo que se sabe de quien pregunta: `{email, isAnonymous}`. En el
// navegador es el usuario de Firebase Auth; en el servidor, lo que contestó
// Identity Toolkit sobre el token.
async function evaluarAccesoCon(user, leer) {
  if (!user) return { entra: false, via: "ninguna", motivo: "sin_sesion" };
  if (user.isAnonymous) return { entra: false, via: "ninguna", motivo: "sesion_anonima" };
  const correo = correoClave(user.email);
  if (!correo) return { entra: false, via: "ninguna", motivo: "sin_correo" };

  // 1) El administrador NO depende de la lista: si la lista queda mal
  //    configurada, tiene que poder entrar a arreglarla.
  if (esAdmin(correo)) return { entra: true, via: "admin" };

  // 2) El interruptor general, para poder abrir desde el panel sin desplegar.
  if (await accesoAbiertoATodos(leer)) return { entra: true, via: "lista" };

  // 3) Patrón en la lista.
  if (await estaEnLaLista(leer, correo)) return { entra: true, via: "lista" };

  // 4) Trabajador que hereda de su patrón.
  const emp = await empleadorDeTrabajador(leer, correo);
  if (emp.email && await estaEnLaLista(leer, emp.email)) {
    return { entra: true, via: "empleador", empleadorEmail: emp.email };
  }
  return ACCESO_DENEGADO;
}

// CommonJS a propósito y exportado nombre por nombre: ver el porqué en
// `checador.js`. Sirve igual al navegador (App.tsx) y a la función de Vercel.
// ── "Suspender cuenta" / "Reactivar cuenta" ─────────────────────────────────
// `activo: false` es CUENTA SUSPENDIDA: no entra a la plataforma (ver
// empleadorDeTrabajador, arriba) ni puede checar (firestore.rules).
// UNA CUENTA SIN EL CAMPO `activo` ESTÁ ACTIVA: así se crearon las de antes, y
// así lo leen las reglas (`cuentaDelTrabajadorActiva`) y el checador
// (`correosConCuentaActiva`). El interruptor escribía `activo: !cuenta.activo`,
// y sobre una cuenta sin el campo eso es `!undefined` = true: el primer clic en
// "Desactivar acceso" la dejaba activa. Ahora se escribe el valor explícito.
function cuentaActiva(cuenta) {
  return !!cuenta && cuenta.activo !== false;
}
// Lo que escribe el interruptor: false al desactivar, true al reactivar.
function activoAlAlternar(cuenta) {
  return !cuentaActiva(cuenta);
}
// ¿El patrón le DESACTIVÓ el acceso? Solo `activo: false` lo dice: una cuenta
// que no se pudo leer (null) no se da por desactivada.
function cuentaDesactivada(cuenta) {
  return !!cuenta && cuenta.activo === false;
}

exports.correoClave = correoClave;
exports.cuentaActiva = cuentaActiva;
exports.activoAlAlternar = activoAlAlternar;
exports.cuentaDesactivada = cuentaDesactivada;
exports.ACCESO_DENEGADO = ACCESO_DENEGADO;
exports.accesoAbiertoATodos = accesoAbiertoATodos;
exports.estaEnLaLista = estaEnLaLista;
exports.empleadorDeTrabajador = empleadorDeTrabajador;
exports.evaluarAccesoCon = evaluarAccesoCon;
